import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Row,
    Col,
    FormGroup,
    Label,
    Button,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    UncontrolledTooltip,
} from "reactstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import CommonTable from "components/ReactTable/Table";
import { showAlert } from "components/Common/commonFunctions";
import {
    discountType,
    customStyle,
    fixedWidthCustomStyle,
    DEFAULT_RECORDS_PER_PAGE,
} from "components/Common/constants";

import {
    getCustomerDiscountList,
    getCustomerDiscountById,
    addCustomerDiscount,
    editCustomerDiscount,
    deleteCustomerDiscount,
} from "actions/customerDiscount";
import RightPanelCard from "components/Common/RightPanelCard";
import { useDispatch, useSelector } from "react-redux";

const discountInitialFields = {
    type: {
        value: null,
        error: "",
    },
    discount: {
        value: 0,
        error: "",
    },
    startDate: {
        value: null,
        error: "",
    },
    endDate: {
        value: null,
        error: "",
    },
};

function CustomerDiscount({
    customerId,
    notify,
    hasAnyError,
    activeTab
}) {
    const dispatch = useDispatch();
    const discountRedux = useSelector(
        (state) => state.customerDiscountList?.customerDiscount || {}
    );

    const [discountData, setDiscountData] = useState(discountInitialFields);

    const [filters, setFilters] = useState({
        type: null,
        startDate: "",
        endDate: "",
        searchText: "",
        filterParams: { filters: [], searchString: "" },
    });

    const [pageNum, setPageNum] = useState(1);

    const [modal, setModal] = useState({
        isOpen: false,
        mode: "",
        data: null,
    });

    const [showLoader, setShowLoader] = useState(false);
    const [discountListState, setDiscountListState] = useState({
        isLoading: true,
        error: "",
        noRecords: false,
        showLoaderOnClick: false,
    });

    useEffect(() => {
        if (!customerId || activeTab !== "discount") return;
        loadDiscounts();
    }, [customerId, pageNum, filters.filterParams, activeTab]);

    useEffect(() => {
        const response = discountRedux;

        if (!response) return;

        if (response?.code === 200) {
            setDiscountListState({
                isLoading: false,
                error: "",
                noRecords: !response?.data?.customerDiscount?.length,
                showLoaderOnClick: false
            });
        } else if (response?.code || response?.message) {
            setDiscountListState({
                isLoading: false,
                error: response?.message || "Failed to load discount list! Please try again.",
                noRecords: false,
                showLoaderOnClick: false
            });
        }
    }, [discountRedux]);


    const loadDiscounts = async () => {
        if (!customerId || activeTab !== "discount") return;

        await getCustomerDiscountList(
            dispatch,
            customerId,
            pageNum,
            filters.filterParams
        );
    };

    const discountList = discountRedux.data?.customerDiscount || [];
    const totalRecords = discountRedux.data?.totalFilteredRecords || 0;
    const formattedDiscountList = discountList.map((item, index) => ({
        ...item,
        srNo: (pageNum - 1) * DEFAULT_RECORDS_PER_PAGE + (index + 1),
    }));

    //This is filter part
    const buildFilterParams = () => {
        const { type, startDate, endDate, searchText } = filters;
        const filterParams = [];

        if (type) {
            filterParams.push({
                field: "DiscountType",
                condition: "eq",
                filterValues: [type.value],
            });
        }

        if (startDate && !endDate) {
            filterParams.push({
                field: "StartDate",
                condition: "gteq",
                filterValues: [
                    new Date(startDate).toISOString().substring(0, 10)
                ],
            });
        }

        if (!startDate && endDate) {
            filterParams.push({
                field: "EndDate",
                condition: "lteq",
                filterValues: [
                    new Date(endDate).toISOString().substring(0, 10)
                ],
            });
        }

        if (startDate && endDate) {
            filterParams.push({
                field: "StartEndDate",
                condition: "eq",
                filterValues: [
                    new Date(startDate).toISOString().substring(0, 10),
                    new Date(endDate).toISOString().substring(0, 10)
                ],
            });
        }

        return {
            filters: filterParams.length ? JSON.stringify(filterParams) : "",
            searchString: searchText || "",
        };
    };

    const handleDiscountSearch = () => {
        if (
            !filters.type &&
            !filters.startDate &&
            !filters.endDate &&
            !filters.searchText.trim()
        ) {
            return;
        }
        const newParams = buildFilterParams();

        if (JSON.stringify(filters.filterParams) !== JSON.stringify(newParams)) {
            setFilters((prev) => ({ ...prev, filterParams: newParams }));
            setPageNum(1);
        }
        setDiscountListState((prev) => ({ ...prev, showLoaderOnClick: true, isLoading: true, }))
    };

    const handleDiscountClear = () => {
        setFilters({
            type: null,
            startDate: "",
            endDate: "",
            searchText: "",
            filterParams: { filters: [], searchString: "" },
        });
        setPageNum(1);
        setDiscountListState((prev) => ({ ...prev, isLoading: true, }))
    };

    const getDiscountValidationError = (name, value, compareValue = "") => {
        switch (name) {
            case "type":
                return !value
                    ? "Please select discount type"
                    : "";

            case "discount":
                return value === "" || value === null
                    ? "Please enter discount"
                    : Number(value) <= 0
                        ? "Discount must be greater than 0"
                        : Number(value) > 100
                            ? "Discount cannot exceed 100%"
                            : "";

            case "startDate":
                return !value
                    ? "Please select start date"
                    : compareValue && new Date(value) > new Date(compareValue)
                        ? "Start date cannot be later than end date"
                        : "";

            case "endDate":
                return !value
                    ? "Please select end date"
                    : compareValue && new Date(value) < new Date(compareValue)
                        ? "End date cannot be earlier than start date"
                        : "";

            default:
                return "";
        }
    };

    const handleDiscountInputChange = (e) => {
        const { name, value } = e.target;
        setDiscountData((prev) => ({
            ...prev,
            [name]: {
                ...prev[name],
                value,
                error: "",
            },
        }));
    };

    const handleLocationBlur = (name, value) => {
        const trimmedValue = typeof value === "string" ? value.trim() : value;
        const error = getDiscountValidationError(
            name,
            value,
            name === "startDate"
                ? discountData.endDate.value
                : name === "endDate"
                    ? discountData.startDate.value
                    : ""
        );

        setDiscountData((prev) => ({
            ...prev,
            [name]: {
                ...prev[name],
                value: trimmedValue,
                error,
            },
        }));
    };

    const validateDiscountFields = () => {
        let updated = { ...discountData };
        let valid = true;

        Object.keys(updated).forEach((key) => {
            const value = updated[key].value;
            const error = getDiscountValidationError(
                key,
                value,
                key === "startDate"
                    ? discountData.endDate.value
                    : key === "endDate"
                        ? discountData.startDate.value
                        : ""
            );

            updated[key] = { ...updated[key], error };
            if (error) valid = false;
        });

        setDiscountData(updated);
        return valid;
    };

    const handleSelectChange = (selected, key) => {
        setDiscountData((prev) => ({
            ...prev,
            [key]: {
                value: selected?.value || null,
                error: "",
            },
        }));
    };

    const handleSaveDiscount = async () => {
        const isValid = validateDiscountFields();
        if (!isValid) return;

        setShowLoader(true);
        const convertDate = (d) => {                //startDate: 2026-05-01  endDate: 2026-05-02
            const [y, m, day] = d.split("-");       //startDate: 05-01-2026  endDate: 05-02-2026
            return `${m}-${day}-${y}`;
        };

        const payload = {
            customerID: customerId,
            discount: Number(discountData.discount.value),
            startDate: convertDate(discountData.startDate.value),
            endDate: convertDate(discountData.endDate.value),
            discountType: discountData.type.value,
        };

        let res;
        if (modal.mode === "Edit") {
            res = await editCustomerDiscount(modal.data.ID, payload);
        } else {
            res = await addCustomerDiscount(payload);
        }

        if (res.code === 200) {
            notify("success", `Discount ${modal.mode === "Edit" ? "updated" : "added"} successfully!`);
            handleCloseModal();
            loadDiscounts();
        } else {
            notify("danger", res.message || "Failed to save discount");
        }

        setShowLoader(false);
    };

    //opening model
    const handleOpenDiscountModal = (mode, data) => {
        setModal({
            isOpen: true,
            mode,           //Add or Edit
            data,           //used for edit mode
        });

        if (data) {
            const newData = Object.fromEntries(
                Object.keys(discountData).map((key) => [    //Object.keys(discountData)=["type","discount","startDate","endDate"]
                    key,
                    {
                        ...discountData[key],
                        value: data[key] ?? null,           // inserts old value into the field
                        error: "",                          // clears old error
                    },
                ])
            );
            /*final output of Object.keys :[
                                                ["type", { value: "PERCENT", error: "" }],
                                                ["discount", { value: 10, error: "" }],
                                                ["startDate", { value: "2026-01-01", error: "" }],
                                                ["endDate", { value: "2026-01-31", error: "" }]
                                            ]
            */
            /*output of Object.fromEntries:
                                            {
                                                type: { value: "PERCENT", error: "" },
                                                discount: { value: 10, error: "" },
                                                startDate: { value: "2026-01-01", error: "" },
                                                endDate: { value: "2026-01-31", error: "" }
                                            }
            */

            setDiscountData(newData);
        } else {
            setDiscountData(discountInitialFields);
        }
    };

    //closing model
    const handleCloseModal = () => {
        setModal({ isOpen: false, mode: "", data: null });
        setDiscountData(discountInitialFields);
    };

    //this is edit customer discount
    const handleEditDiscount = async (item) => {
        setShowLoader(true);

        const res = await getCustomerDiscountById(customerId, item.ID);

        if (res.code === 200) {
            const d = res.data;

            handleOpenDiscountModal("Edit", {
                ID: d.ID,
                type: d.discountType,
                discount: d.discount,
                startDate: convertToInputDate(d.startDate),
                endDate: convertToInputDate(d.endDate),
            });
            setShowLoader(false);
        } else {
            notify("danger", res.message || "Failed to load discount details.");
            setShowLoader(false);
        }
    };

    //this is delete customer discount
    const confirmDeleteDiscount = async (item) => {
        setShowLoader(true);

        const res = await deleteCustomerDiscount(customerId, item.ID);

        if (res.code === 200) {
            notify("success", res?.message || "Discount deleted successfully.");
            loadDiscounts();
        } else {
            notify("danger", res?.message || "Failed to delete discount.");
        }

        setShowLoader(false);
    };

    const handleDiscountDelete = (item) => {
        showAlert({
            title: "Are you sure you want to delete this discount?",
            message: `The ${(discountType.find((d) => d.value === item.discountType) || {}).smallCase} of ${item.discount}% for this customer will be permanently deleted.`,
            type: "warning",
            confirmStyle: "danger",
            cancelStyle: "secondary",
            confirmText: "Yes, Delete",
            cancelText: "Cancel",
            onConfirm: () => confirmDeleteDiscount(item),
            onCancel: () => { },
        });
    };

    //required date transformation functions
    const convertToInputDate = (dateStr) => {
        const d = new Date(dateStr);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const getNextDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
    };

    const disColumns = [
        { Header: "Sr. no.", accessor: "srNo", className: "col-discount-srNo", },
        {
            Header: "Discount Type",
            accessor: "discountType",
            className: "col-discount-type",
            Cell: ({ value }) => (
                <span>{(discountType.find((d) => d.value === value) || {}).label}</span>
            ),
        },
        { Header: "Discount (%)", accessor: "discount", className: "col-discount text-right", rightAlign: true, },
        {
            Header: "Start Date",
            accessor: "startDate",
            className: "col-type",
            centerAlign: true,
            Cell: ({ value }) => (
                <span>
                    {new Date(value).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                    })}
                </span>
            ),
        },
        {
            Header: "End Date",
            accessor: "endDate",
            className: "col-type-end",
            Cell: ({ value }) => (
                <span>
                    {new Date(value).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                    })}
                </span>
            ),
        },
        {
            Header: "Actions",
            accessor: "status",
            className: "text-center col-discount-action",
            disableSortBy: true,
            Cell: ({ row }) => (
                <div className="d-flex justify-content-start align-items-center w-100">
                    <div>
                        <Button
                            className="btn-icon icon-info"
                            color="link"
                            id={`edit${row.original.ID}`}
                            size="sm"
                            onClick={async (e) => {
                                e.stopPropagation();
                                await handleEditDiscount(row.original);
                            }}
                        >
                            <i className="tim-icons icon-pencil tim-md" />
                        </Button>
                        <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
                            Edit
                        </UncontrolledTooltip>
                    </div>
                    <span className="mx-1 mt-0 text-muted">|</span>
                    <div>
                        <Button
                            className="btn-icon icon-danger"
                            color="link"
                            id={`reset${row.original.ID}`}
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDiscountDelete(row.original);
                            }}
                        >
                            <i className="tim-icons icon-trash-simple tim-md" />
                        </Button>
                        <UncontrolledTooltip delay={0} target={`reset${row.original.ID}`}>
                            Delete
                        </UncontrolledTooltip>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="d-flex justify-content-end">
                <Button
                    color="default"
                    size="sm"
                    onClick={() => handleOpenDiscountModal("Add", null)}
                >
                    <i className="fa fa-plus mr-2" />
                    Add Discount
                </Button>
            </div>
            <div className="d-flex flex-wrap align-items-center">
                {/* Discount Type */}
                <Select
                    className="mr-3 mb-2 filter-select"
                    options={discountType}
                    placeholder="Discount type"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    value={filters.type}
                    autoComplete="off"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleDiscountSearch();
                        }
                    }}
                    onChange={(selected) =>
                        setFilters((prev) => ({
                            ...prev,
                            type: selected,
                        }))
                    }
                />

                {/* Start Date */}
                <div className="d-flex align-items-center mr-3 mb-2">
                    <Label className="text-muted mb-0 mr-2">
                        Start Date
                    </Label>

                    <DatePicker
                        selected={filters.startDate || null}
                        onChange={(date) =>
                            setFilters((prev) => ({
                                ...prev,
                                startDate: date,
                            }))
                        }
                        isClearable
                        placeholderText="Select start date"
                        autoComplete="off"
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                    />
                </div>

                {/* End Date */}
                <div className="d-flex align-items-center mr-2 mb-2">
                    <Label className="text-muted mb-0 mr-2">
                        End Date
                    </Label>

                    <DatePicker
                        selected={filters.endDate || null}
                        onChange={(date) =>
                            setFilters((prev) => ({
                                ...prev,
                                endDate: date,
                            }))
                        }
                        isClearable
                        placeholderText="Select end date"
                        autoComplete="off"
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        minDate={filters.startDate}
                    />
                </div>

                {/* Search Button */}
                <Button
                    color="default"
                    className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                    onClick={handleDiscountSearch}
                >
                    {discountListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                    ) : (
                        <>
                            <i className="fa fa-search mr-1" />
                            Search
                        </>
                    )}
                </Button>

                {/* Clear Button */}
                {(filters.filterParams?.filters?.length ||
                    filters.filterParams?.searchString) && (
                        <Button
                            color="default"
                            className="mb-2 filter-btn btn-simple mt-0"
                            onClick={handleDiscountClear}
                        >
                            <i className="fa fa-times mr-1" /> Clear
                        </Button>
                    )}
            </div>

            <div className="mt-3 mb-3 mx-1">
                {totalRecords ? (
                    !(
                        filters.filterParams?.filters?.length ||
                        filters.filterParams?.searchString
                    ) ? (
                        <span className="font-weight-bold">
                            Total Discounts : {totalRecords}
                        </span>
                    ) : (
                        <span className="font-weight-bold">
                            Showing {totalRecords} of {totalRecords} Discounts
                        </span>
                    )
                ) : null}
            </div>

            {discountListState.isLoading ? (
                <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl" />
                </div>
            ) : discountListState.error ? (
                <div className="loaderContainer text-danger">{discountListState.error}</div>
            ) : discountListState.noRecords ? (
                <div className="loaderContainer text-muted">No customer discounts added yet.</div>
            ) : (
                <CommonTable
                    data={formattedDiscountList}
                    resizable={false}
                    columns={disColumns}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    showPaginationBottom={true}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(totalRecords / DEFAULT_RECORDS_PER_PAGE)}
                    className="-striped -highlight table-hover"
                />
            )}

            <RightPanelCard
                isOpen={modal.isOpen}
                onClose={handleCloseModal}
            >
                <Card className="mb-0">
                    <CardHeader
                        className="d-flex align-items-center justify-content-between mb-2"
                        tag="h4"
                    >
                        {modal.mode} Customer Discount
                        <i
                            className="fa fa-close text-muted pointer"
                            onClick={handleCloseModal}
                        />
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <Col md="6" className="mb-3">
                                <FormGroup
                                    className={`has-label ${discountData.type.error ? "has-danger" : ""
                                        } d-flex flex-column align-items-start`}
                                >
                                    <Label>
                                        Discount Type <span className="validation-error">*</span>
                                    </Label>
                                    <Select
                                        className={`text-left ${discountData.type.error ? "is-invalid" : ""
                                            }`}
                                        options={discountType}
                                        placeholder="Select discount type"
                                        classNamePrefix="react-select"
                                        styles={customStyle}
                                        value={discountType.find(
                                            (option) => option.value === discountData.type.value
                                        )}
                                        onChange={(selectedOption) =>
                                            handleSelectChange(selectedOption, "type")
                                        }
                                        onBlur={() =>
                                            handleLocationBlur(
                                                "type",
                                                discountData.type.value
                                            )
                                        }
                                    />
                                    {discountData.type.error && (
                                        <small className="validation-error">
                                            {discountData.type.error}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>

                            <Col md="6" className="mb-3">
                                <FormGroup
                                    className={`has-label ${discountData.discount.error ? "has-danger" : ""
                                        } d-flex flex-column align-items-start`}
                                >
                                    <Label>
                                        Discount (%) <span className="validation-error">*</span>
                                    </Label>
                                    <InputGroup className="input-group">
                                        <Input
                                            name="discount"
                                            type="number"
                                            placeholder="Enter discount"
                                            value={discountData.discount.value}
                                            onChange={handleDiscountInputChange}
                                            onBlur={(e) =>
                                                handleLocationBlur(
                                                    e.target.name,
                                                    e.target.value
                                                )
                                            }
                                            min={0}
                                        />
                                        <InputGroupAddon addonType="append">
                                            <InputGroupText className="d-flex align-items-center">
                                                %
                                            </InputGroupText>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {discountData.discount.error && (
                                        <small className="validation-error">
                                            {discountData.discount.error}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>

                            <Col md="6" className="mb-2">
                                <FormGroup
                                    className={`has-label ${discountData.startDate.error ? "has-danger" : ""
                                        } d-flex flex-column align-items-start`}
                                >
                                    <Label for="startDate">
                                        Start Date <span className="validation-error">*</span>
                                    </Label>
                                    <Input
                                        id="startDate"
                                        name="startDate"
                                        type="date"
                                        placeholder="Select start date"
                                        value={discountData.startDate.value}
                                        onChange={handleDiscountInputChange}
                                        onBlur={(e) =>
                                            handleLocationBlur(
                                                e.target.name,
                                                e.target.value
                                            )
                                        }
                                    />
                                    {discountData.startDate.error && (
                                        <small className="validation-error">
                                            {discountData.startDate.error}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>

                            <Col md="6" className="mb-3">
                                <FormGroup
                                    className={`has-label ${discountData.endDate.error ? "has-danger" : ""
                                        } d-flex flex-column align-items-start`}
                                >
                                    <Label for="endDate">
                                        End Date <span className="validation-error">*</span>
                                    </Label>
                                    <Input
                                        id="endDate"
                                        name="endDate"
                                        type="date"
                                        placeholder="Select end date"
                                        value={discountData.endDate.value}
                                        onChange={handleDiscountInputChange}
                                        onBlur={(e) =>
                                            handleLocationBlur(
                                                e.target.name,
                                                e.target.value
                                            )
                                        }
                                        disabled={!discountData.startDate.value}
                                        min={getNextDate(discountData.startDate.value)}
                                    />
                                    {discountData.endDate.error && (
                                        <small className="validation-error">
                                            {discountData.endDate.error}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                    </CardBody>

                    <CardFooter className="d-flex justify-content-end mb-0">
                        <Button
                            color="secondary"
                            onClick={handleCloseModal}
                            className="mr-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="default"
                            onClick={handleSaveDiscount}
                            disabled={hasAnyError(discountData)}
                        >
                            {showLoader ? (
                                <i className="fa fa-spinner fa-spin" />
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </CardFooter>
                </Card>
            </RightPanelCard>
        </>
    )
};

export default CustomerDiscount;